import React, {useCallback, useEffect, useMemo, useState} from "react"
import {useLocalization} from "../../../../hooks/localization"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {useQuestion} from "../../../../hooks/question"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useControlSelector} from "../../../../hooks/control-selector"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SettingReadOnlyRowComponent} from "../../../../components/Settings/SettingReadOnlyRowComponent"
import {useSelector} from "react-redux"
import {SelectItem} from "../../../../components/Select"
import {FrameUIEntity, FrameUIType} from "../../../../store/control/state"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"

export function FrameEditorContentEditScreen() {
    const {t} = useLocalization()
    const params = useParams<{id: string; device_id: string}>()
    const question = useQuestion()
    const location = useLocation<{controlId: string; bridgeDeviceId: string; virtualControlId: string}>()
    const {success, failure} = useMessage()
    const history = useHistory()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const [rpc] = useRPC()
    const controlList = useControlSelector()
    const frameList = useSelector(state => state.control.frameUIList)

    const PAGE_NUMBER_LIST = useMemo(
        () =>
            Array.from({length: 40}, (_, i) => ({
                label: `${i + 1}`,
                value: i + 1
            })),
        []
    )

    const filterPageNumber = useMemo(() => {
        return PAGE_NUMBER_LIST.filter(item =>
            frameList
                .filter(items => items?.bridge_device_id === params?.device_id)
                .every(items => items.page_number !== item.value)
        )
    }, [frameList, params, PAGE_NUMBER_LIST])

    const backUrl = useMemo(
        () => `/settings/frame-editor/${params?.device_id || location?.state?.bridgeDeviceId}`,
        [params, location]
    )

    const controlTypes = useSelector(state => state.control.controlTypeList)
    const frameUIEntityList = useSelector(state => state.control.frameUIList)
    const currentFrameUIEntity = useMemo(() => {
        return frameUIEntityList.find(e => e.id === params.id || e.relational_id === location?.state?.controlId) || null
    }, [frameUIEntityList, params, location])
    const currentControl = useMemo(() => {
        if (currentFrameUIEntity?.type !== "control" && currentFrameUIEntity?.type !== "virtualControl") {
            return null
        }
        return (
            controlList.find(
                c => c.id === currentFrameUIEntity?.relational_id || c.id === location?.state?.controlId
            ) || null
        )
    }, [location, controlList, currentFrameUIEntity])
    const controlTypeName =
        useMemo(
            () => controlTypes.find(t => t.id === currentControl?.control.type_id),
            [controlTypes, currentControl?.control.type_id]
        )?.name || ""

    const CONTENT_TYPES = useMemo<{label: string; value: FrameUIType}[]>(
        () => [
            {label: t("weather"), value: "weather"},
            {label: t("scenario"), value: "scenario"}
        ],
        [t]
    )

    const ICON_COLOR_TYPES = useMemo<{label: string; value: string}[]>(
        () => [
            {label: t("white"), value: "0"},
            {label: t("black"), value: "1"},
            {label: t("gold"), value: "2"},
            {label: t("silver"), value: "3"},
            {label: t("rose"), value: "4"}
        ],
        [t]
    )

    const [displayName, setDisplayName] = useState<string>("")
    const [contentType, setContentType] = useState<SelectItem<FrameUIType>>(null)
    const [iconColorType, setIconColorType] = useState<SelectItem<string>>(ICON_COLOR_TYPES[0] || null)
    const [pageNumber, setPageNumber] = useState<SelectItem<number>>(null)
    const [isAnimationActive, setIsAnimationActive] = useState(false)

    const deviceList = useSelector(state => state.control.bridgeDeviceList)
    const bridgeDevice = useMemo(() => deviceList.find(d => d.id === params.device_id), [deviceList, params.device_id])

    const onBlur = useCallback(() => setActiveInput(null), [setActiveInput])
    const onBackPressed = useCallback(() => history.push(backUrl), [history, backUrl])

    const isFieldsValid = useCallback((): boolean => {
        if (contentType?.value === "scenario" && !displayName) {
            failure(t("pls_enter_a_valid_display_name"))
            return false
        }

        if (pageNumber == null) {
            failure(t("pls_select_a_page"))
            return false
        }

        return true
    }, [t, contentType?.value, failure, pageNumber, displayName])

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }

        const uiEntity: FrameUIEntity = {
            ...currentFrameUIEntity,
            id: currentFrameUIEntity?.id || undefined,
            type: currentFrameUIEntity == null ? contentType?.value : currentFrameUIEntity?.type,
            bridge_device_id: params?.device_id,
            relational_id: currentFrameUIEntity?.relational_id,
            page_number: pageNumber?.value,
            is_animation_active: isAnimationActive,
            icon_color_type: iconColorType?.value,
            extras:
                displayName?.length === 0
                    ? null
                    : {
                          ...currentFrameUIEntity?.extras,
                          name: displayName
                      }
        }

        rpc.updateFrameUI(uiEntity)
            .then(() => {
                success(t(currentFrameUIEntity ? "ui_element_successfully_edited" : "ui_element_successfully_created"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        success,
        onBackPressed,
        failure,
        isFieldsValid,
        currentFrameUIEntity,
        isAnimationActive,
        rpc,
        iconColorType?.value,
        contentType?.value,
        displayName,
        pageNumber?.value,
        params?.device_id
    ])

    const onDeletePressed = useCallback(() => {
        question(t("are_you_sure_to_delete_ui_element"), () => {
            return rpc
                .deleteFrameUI(currentFrameUIEntity.id)
                .then(() => {
                    success(t("ui_element_successfully_deleted"))
                    onBackPressed()
                })
                .catch(failure)
        })
    }, [t, failure, onBackPressed, question, rpc, currentFrameUIEntity?.id, success])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                className: "mulberry-wood",
                onClick: onDeletePressed,
                hidden: currentControl != null || currentFrameUIEntity == null
            },
            {
                text: t("save"),
                className: "green",
                onClick: onCreatePressed
            },
            {
                text: t("cancel"),
                className: "red",
                url: backUrl
            }
        ],
        [t, onDeletePressed, onCreatePressed, backUrl, currentFrameUIEntity, currentControl]
    )

    useEffect(() => {
        if (currentFrameUIEntity == null) {
            return
        }
        setDisplayName(currentFrameUIEntity?.extras?.name)
        setContentType(CONTENT_TYPES.find(t => t?.value === currentFrameUIEntity?.type))
        setPageNumber(PAGE_NUMBER_LIST.find(o => currentFrameUIEntity?.page_number === o?.value) || null)
        setIconColorType(ICON_COLOR_TYPES.find(o => currentFrameUIEntity?.icon_color_type === o?.value) || null)
        setIsAnimationActive(currentFrameUIEntity?.is_animation_active)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                currentInput={currentInput}
                className="service-edit"
                backUrl={backUrl}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingReadOnlyRowComponent title={t("device_name")} value={bridgeDevice?.name} />
                        {renderIf(currentFrameUIEntity?.type === "scenario" || contentType?.value === "scenario")(
                            () => (
                                <SettingInputComponent
                                    placeholder={t("write_display_name")}
                                    label={t("display_name")}
                                    value={displayName}
                                    setValue={setDisplayName}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            )
                        )}

                        {renderIf(currentControl != null)(() => (
                            <SettingReadOnlyRowComponent
                                title={t("control_name")}
                                value={currentControl?.control.name}
                            />
                        ))}
                        {renderIf(currentFrameUIEntity == null)(
                            () => (
                                <SettingSelectComponent
                                    label={t("type")}
                                    options={CONTENT_TYPES}
                                    onChange={setContentType}
                                    value={contentType}
                                />
                            ),
                            () => (
                                <SettingReadOnlyRowComponent
                                    title={t("type")}
                                    value={currentControl != null ? controlTypeName : contentType?.label}
                                />
                            )
                        )}
                        <SettingSelectComponent
                            label={t("page_number")}
                            options={filterPageNumber}
                            onChange={setPageNumber}
                            value={pageNumber}
                        />
                        <SettingSelectComponent
                            label={t("icon_color_type")}
                            options={ICON_COLOR_TYPES}
                            onChange={setIconColorType}
                            value={iconColorType}
                        />
                        <SettingCheckBoxComponent
                            label={t("is_animation_active")}
                            set={() => setIsAnimationActive(p => !p)}
                            value={isAnimationActive}
                        />
                    </div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
        </PasswordContainer>
    )
}
